import React from "react";
import type { AnsweredQuestion } from "@/api/q-and-a";
import { filterFoundAnswerGroups } from "../utils/answerGroupUtils";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { stringToPastelColor } from "@/lib/utils";
import { Link } from "react-router-dom";
import { BookOpenCheck, PenLine, User, Edit2, Trash2, Search, X } from "lucide-react";
import type { Highlight } from "react-pdf-highlighter-extended";

interface QuestionPaneProps {
  selectedQuestionId?: string;
  questions: AnsweredQuestion[];
  userHighlights: Highlight[];
  isVisible: boolean;
  onToggle: () => void;
  onQuestionClick: (questionId: string) => void;
  onHighlightClick: (highlight: Highlight) => void;
  canEditHighlight: (highlightId: string) => boolean;
  onEditHighlight: (highlightId: string, newComment: string) => Promise<void>;
  onDeleteClick: (highlightId: string) => void;
}

interface DocumentHighlight extends Highlight {
  userInfo?: {
    id: string;
    name: string;
    email: string;
  };
  comment?: { text: string; emoji: string };
}

export const QuestionPane: React.FC<QuestionPaneProps> = ({
  selectedQuestionId,
  questions,
  userHighlights,
  isVisible,
  onToggle,
  onQuestionClick,
  onHighlightClick,
  canEditHighlight,
  onEditHighlight,
  onDeleteClick,
}) => {
  const [showQuestionsWithoutAnswers, onToggleQuestionsWithoutAnswers] =
    React.useState(false);
  const [questionsExpanded, setQuestionsExpanded] = React.useState(true);
  const [highlightsExpanded, setHighlightsExpanded] = React.useState(true);
  const [activeHighlight, setActiveHighlight] = React.useState<{
    id: string | null;
    type: "citation" | "user" | null;
  }>({ id: null, type: null });
  const [editingHighlightId, setEditingHighlightId] = React.useState<
    string | null
  >(null);
  const [editComment, setEditComment] = React.useState<string>("");
  const [searchTerm, setSearchTerm] = React.useState<string>("");

  // Ref for the scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Effect to scroll to selected question
  React.useEffect(() => {
    if (selectedQuestionId && isVisible && scrollContainerRef.current) {
      // Small delay to ensure the DOM is updated and questions are expanded
      const timer = setTimeout(() => {
        const questionElement = document.querySelector(
          `[data-question-id="${selectedQuestionId}"]`,
        );
        
        if (questionElement) {
          // Ensure questions section is expanded first
          setQuestionsExpanded(true);
          
          // Small additional delay to account for expansion animation
          setTimeout(() => {
            questionElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }, 100);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [selectedQuestionId, isVisible]);

  const handleEditStart = (highlight: DocumentHighlight) => {
    setEditingHighlightId(highlight.id);
    setEditComment(highlight.comment?.text || "");
  };

  const handleEditSave = async (highlightId: string) => {
    try {
      await onEditHighlight(highlightId, editComment);
      setEditingHighlightId(null);
      setEditComment("");
    } catch (error) {
      console.error("Failed to update highlight:", error);
    }
  };

  const handleEditCancel = () => {
    setEditingHighlightId(null);
    setEditComment("");
  };

  const mappedQuestions = questions.map((question) => {
    // Filter to only found answers (questions are already filtered by document upstream)
    const foundGroups = filterFoundAnswerGroups(question.answer_groups);
    
    return {
      ...question,
      answer_groups: foundGroups,
      answerFound: foundGroups.length > 0,
    };
  });
  
  // TODO - we may need to move this logic to BE if there are a lot of questions or highlights
  const filteredByAnswers = showQuestionsWithoutAnswers
    ? mappedQuestions
    : mappedQuestions.filter((question) => question.answerFound);
    
  // Filter by search term
  const filteredResults = searchTerm.trim() === ""
    ? filteredByAnswers
    : filteredByAnswers.filter((question) =>
        question.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (question.question_category?.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      );

  // Filter highlights by search term
  const filteredHighlights = searchTerm.trim() === ""
    ? userHighlights
    : userHighlights.filter((highlight) => {
        const documentHighlight = highlight as DocumentHighlight;
        const commentText = documentHighlight.comment?.text || "";
        const userName = documentHighlight.userInfo?.name || "";
        return (
          commentText.toLowerCase().includes(searchTerm.toLowerCase()) ||
          userName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });

  if (!isVisible) {
    return (
      <div className="flex w-8 items-center justify-center border-r border-gray-300 bg-gray-100">
        <button
          onClick={onToggle}
          className="rotate-90 p-2 text-gray-600 hover:text-gray-800"
          title="Show Questions"
        >
          ▲
        </button>
      </div>
    );
  }

  // Check if there's anything to search
  const hasSearchableContent = questions.length > 0 || userHighlights.length > 0;

  return (
    <div className="flex w-1/4 flex-col border-r border-gray-300 bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            Questions & Highlights
          </h2>
          <button
            onClick={onToggle}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Hide Questions"
          >
            ✕
          </button>
        </div>
        <div className="relative">
          <Search className={`absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 ${!hasSearchableContent ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
          <Input
            placeholder="Search questions and highlights..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-8 text-sm ${searchTerm ? 'pr-8' : ''}`}
            disabled={!hasSearchableContent}
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchTerm("")}
              title="Clear search"
            >
              <X size={12} />
            </Button>
          )}
        </div>
      </div>

      {/* Content with expandable sections */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        {/* Questions Section */}
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
              <BookOpenCheck className="h-5 w-5" />
              Questions ({filteredResults.length})
            </h3>
            <button
              onClick={() => setQuestionsExpanded(!questionsExpanded)}
              className="p-1 text-gray-500 hover:text-gray-700"
              title={questionsExpanded ? "Collapse" : "Expand"}
            >
              {questionsExpanded ? "▼" : "▶"}
            </button>
          </div>
          {questionsExpanded && (
            <>
              {/* Toggle for questions without answers */}
              <div className="flex items-center space-x-2 px-4 pb-3 text-xs">
                <Switch
                  id="show-questions-without-answers"
                  checked={showQuestionsWithoutAnswers}
                  onCheckedChange={onToggleQuestionsWithoutAnswers}
                />
                <label
                  htmlFor="show-questions-without-answers"
                  className="cursor-pointer text-gray-600"
                >
                  Show questions without answers
                </label>
              </div>
              {/* Questions List */}
              {questions.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No questions found for this document
                </div>
              ) : filteredResults.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm.trim() !== "" 
                    ? "No questions match your search"
                    : "No questions with answers found"
                  }
                </div>
              ) : (
                <div className="space-y-2 p-4 pt-0">
                  {filteredResults.map((question) => {
                    const isActive =
                      selectedQuestionId === question.question_id;
                    const baseClasses = "p-3 border border-l-4";
                    const selectedClasses =
                      "border-green-600 bg-green-50 hover:bg-green-100";
                    const unselectedClasses = "border-gray-200";
                    const interactiveClasses = question.answerFound
                      ? "cursor-pointer hover:bg-gray-50"
                      : "bg-gray-100";

                    return (
                      <div
                        key={question.question_id}
                        data-question-id={question.question_id}
                        className={`${baseClasses} ${isActive ? selectedClasses : unselectedClasses} ${interactiveClasses}`}
                        onClick={() => {
                          if (question.answerFound) {
                            setActiveHighlight({
                              id: question.question_id,
                              type: "citation",
                            });
                            onQuestionClick(question.question_id);
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          {question.answer_mode === "signature" ? (
                            <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                          ) : (
                            <BookOpenCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                          )}
                          <div className="flex-1">
                            <div className="mb-1 text-sm font-medium text-gray-800">
                              <Link
                                to={`/question-bank/?qid=${question.question_id}`}
                                className="text-linkblue hover:underline"
                                target="_blank"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {question.question_text || (question.answer_mode === "signature" ? "Signature detection" : "Untitled question")}
                              </Link>
                            </div>
                            {question.answerFound ? (
                              <div className="text-sm text-gray-500">
                                {question.answer_groups.length} answer group{question.answer_groups.length !== 1 ? "s" : ""}
                              </div>
                            ) : (
                              <div className="text-sm italic text-muted-foreground">
                                Answer not found
                              </div>
                            )}
                            {question.question_category && (
                              <Badge
                                className="h-xs mt-1 shrink-0 overflow-ellipsis font-normal"
                                style={{
                                  backgroundColor: stringToPastelColor(
                                    question.question_category.id as string,
                                  ),
                                }}
                              >
                                {question.question_category.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* User Highlights Section */}
        <div>
          <div className="flex items-center justify-between p-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
              <User className="h-5 w-5" />
              User Highlights ({filteredHighlights.length})
            </h3>
            <button
              onClick={() => setHighlightsExpanded(!highlightsExpanded)}
              className="p-1 text-gray-500 hover:text-gray-700"
              title={highlightsExpanded ? "Collapse" : "Expand"}
            >
              {highlightsExpanded ? "▼" : "▶"}
            </button>
          </div>
          {highlightsExpanded && (
            <div className="space-y-2 p-4">
              {filteredHighlights.length === 0 ? (
                <div className="text-center text-gray-500">
                  {searchTerm.trim() !== "" 
                    ? "No highlights match your search"
                    : "No user highlights found"
                  }
                </div>
              ) : (
                filteredHighlights.map((highlight) => {
                  const isActive =
                    activeHighlight.id === highlight.id &&
                    activeHighlight.type === "user";
                  const isEditing = editingHighlightId === highlight.id;
                  const DocumentHighlight = highlight as DocumentHighlight;
                  const canEdit = canEditHighlight(highlight.id);

                  return (
                    <div
                      key={highlight.id}
                      className={`border border-l-4 p-3 transition-all duration-300 ${
                        isActive
                          ? "border-orange-600 bg-orange-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setActiveHighlight({
                          id: highlight.id,
                          type: "user",
                        });
                        onHighlightClick(highlight);
                        // Optionally reset after transition
                        setTimeout(
                          () => setActiveHighlight({ id: null, type: null }),
                          1000,
                        );
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <User className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-gray-800">
                              {DocumentHighlight.userInfo?.name ||
                                "User Highlight"}
                            </div>
                            {canEdit && !isEditing && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditStart(highlight);
                                  }}
                                  title="Edit highlight"
                                >
                                  <Edit2 size={12} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteClick(highlight.id);
                                  }}
                                  title="Delete highlight"
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            )}
                          </div>

                          {isEditing ? (
                            <div className="mt-2 space-y-2">
                              <textarea
                                value={editComment}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setEditComment(e.target.value)}
                                className="w-full rounded border border-gray-300 p-2 text-sm"
                                rows={3}
                                placeholder="Edit your comment..."
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSave(highlight.id);
                                  }}
                                  className="h-7 px-3 text-xs"
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCancel();
                                  }}
                                  className="h-7 px-3 text-xs"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1 cursor-pointer text-sm text-gray-600">
                              {(() => {
                                const commentText = (
                                  highlight as DocumentHighlight
                                ).comment?.text;
                                if (!commentText) return "No comment";
                                return commentText.length > 60
                                  ? `${commentText.substring(0, 60)}...`
                                  : commentText;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  })
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
